import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { PlanStakeholder } from '../../types/stakeholder.types';
import { useAppQuery } from '../use-app-query.hook';

type StakeholdersWithGrants = {
  companyId: string;
  currentPage?: number;
  take?: number;
  vestByDate?: Date;
};

type StakeholdersWithGrantsResponse = {
  stakeholders: PlanStakeholder[];
  totalPages: number;
};

export const useStakeholdersWithGrants = ({
  companyId,
  currentPage = 0,
  take = 100,
  vestByDate,
}: StakeholdersWithGrants) => {
  const queryClient = useQueryClient();

  const { data, isLoading } = useAppQuery<StakeholdersWithGrantsResponse>({
    queryKey: [QueryKey.GET_STAKEHOLDERS_WITH_GRANTS, companyId, { currentPage, take, vestByDate }],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'stakeholders-with-grants'] },
        {
          queryParams: {
            vestByDate,
            ...(take && currentPage
              ? {
                  take,
                  skip: (currentPage >= 1 ? currentPage - 1 : 0) * take,
                }
              : {}),
          },
        },
      ),
    enabled: !!companyId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_STAKEHOLDERS_WITH_GRANTS] });
  }, [queryClient]);

  return {
    stakeholders: data?.stakeholders || [],
    totalPages: data?.totalPages,
    invalidateQuery,
    isLoading,
  };
};
